import { observer } from 'mobx-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, RadioGroup, Typography, useTheme } from '@mui/material';
import SimpleFileUpload from 'components/FileUpload/layout/SimpleFileUpload';
import CustomPopover, { CustomPopoverProps } from 'components/Popover';
import { Field, Formik } from 'formik';
import { paletteBackground } from 'models/Theme';
import store from 'store';
import defaultTheme from 'theme';
import getModalGradient from 'utils/getModalGradient';

import ThemeBackgroundImageItem from './BackgroundImageItem';
import ProfilePopoverBanner from './Banner';
import ProfilePopoverColorPicker from './ColorPickerItem';
import {
	StyledThemePopoverDescriptionWrap,
	StyledThemePopoverForm,
	StyledThemePopoverUploadFileIcon,
	ThemeBackgroundImageList,
} from './styled';

interface PropsType extends CustomPopoverProps {}

const customBackgroundGradient = 'linear-gradient(111.25deg, #E2E8FF 1.75%, #DFD8FA 97.72%)';

function ProfileThemePopover({ onCloseButtonClick, ...props }: PropsType) {
	const theme = useTheme();
	const { t } = useTranslation();

	const background = theme.palette.background.default;

	const initialValues = {
		background: background.startsWith('url') ? 'customImage' : background,
		backgroundImage: background.startsWith('url') ? background : '',
		banner: theme.palette.banner,
		modalGradient: [theme.palette.modal.color1, theme.palette.modal.color2],
		primary: theme.palette.primary.main,
		text: theme.palette.text.primary,
	};

	const handleSubmit = (data: any) => {
		store.theme.set(data);
		onCloseButtonClick();
	};

	const resetToDefault = () => {
		store.theme.reset();
		onCloseButtonClick();
	};

	return (
		<CustomPopover
			title={t('themeModal.title')}
			anchorReference="anchorPosition"
			anchorPosition={{ top: 80, left: window.innerWidth - 24 }}
			anchorOrigin={{
				vertical: 'top',
				horizontal: 'right',
			}}
			transformOrigin={{
				vertical: 'top',
				horizontal: 'right',
			}}
			onCloseButtonClick={onCloseButtonClick}
			slotProps={{ paper: { sx: { width: 'min(455px, calc(100% - 16px)) !important' } } }}
			{...props}
		>
			<StyledThemePopoverDescriptionWrap elevation={2}>
				<Typography variant="body2">{t('themeModal.youAreResponsible')}</Typography>
			</StyledThemePopoverDescriptionWrap>
			<Formik initialValues={initialValues} onSubmit={handleSubmit}>
				{({ values, setFieldValue, initialValues }) => {
					const handleBackgroundChange = (event: React.ChangeEvent<HTMLInputElement>) => {
						const newBackground = event.target.value;

						// If the new background is 'concentration' or 'space',
						// and the initial values were set to those with default colors
						if (
							(newBackground === paletteBackground.concentration ||
								newBackground === paletteBackground.space) &&
							values.background !== newBackground
						) {
							// Set default colors if not already customized
							setFieldValue('background', newBackground);
							if (newBackground === paletteBackground.concentration) {
								setFieldValue('modalGradient', [
									defaultTheme.palette.modal.color1,
									defaultTheme.palette.modal.color2,
								]);
								setFieldValue('primary', defaultTheme.palette.primary.main);
								setFieldValue('text', defaultTheme.palette.text.primary);
							} else if (newBackground === paletteBackground.space) {
								setFieldValue('modalGradient', ['#C3E2FF', '#FFD9FD']);
								setFieldValue('primary', defaultTheme.palette.primary.main);
								setFieldValue('text', defaultTheme.palette.text.primary);
							}
						} else {
							setFieldValue('background', newBackground);
						}
					};

					return (
						<StyledThemePopoverForm>
							<Field
								as={RadioGroup}
								name="background"
								value={values.background}
								onChange={handleBackgroundChange}
							>
								<ThemeBackgroundImageList>
									<ThemeBackgroundImageItem
										labelBg={paletteBackground.concentration}
										labelText={t('themeModal.concentration')}
										value={paletteBackground.concentration}
									/>
									<ThemeBackgroundImageItem
										labelBg={paletteBackground.space}
										labelText={t('themeModal.space')}
										value={paletteBackground.space}
									/>
									<SimpleFileUpload
										name="backgroundImage"
										maxSize={25}
										onDropRejected={() => setFieldValue('background', initialValues.background)}
										onFileDialogCancel={() => setFieldValue('background', initialValues.background)}
										sx={{ flex: 1 }}
									>
										<ThemeBackgroundImageItem
											labelBg={customBackgroundGradient}
											label={<StyledThemePopoverUploadFileIcon />}
											labelText={t('themeModal.image')}
											value="customImage"
										/>
									</SimpleFileUpload>
								</ThemeBackgroundImageList>
							</Field>
							<ProfilePopoverBanner name="banner" />
							<ProfilePopoverColorPicker
								colors={values.modalGradient}
								label={t('themeModal.gradient')}
								background={getModalGradient(values.modalGradient[0], values.modalGradient[1])}
								onChange={(value) => setFieldValue('modalGradient', value)}
							/>
							<ProfilePopoverColorPicker
								colors={values.primary}
								label={t('themeModal.main')}
								background={values.primary}
								onChange={(value) => setFieldValue('primary', value)}
							/>
							<ProfilePopoverColorPicker
								colors={values.text}
								label={t('themeModal.text')}
								background={values.text}
								onChange={(value) => setFieldValue('text', value)}
							/>
							<Button variant="contained" color="primary" type="submit">
								{t('shared.save')}
							</Button>
							<Button variant="contained" color="error" onClick={resetToDefault}>
								{t('themeModal.reset')}
							</Button>
						</StyledThemePopoverForm>
					);
				}}
			</Formik>
		</CustomPopover>
	);
}

export default observer(ProfileThemePopover);
